// La Frontera — vendedor obligatorio + lanzamiento WhatsApp
// Regla crítica: el click handler debe ser 100% síncrono. Cualquier await
// rompe el "user activation" que iOS Safari requiere para abrir la app de
// WhatsApp, así que la URL se precalcula ante cualquier cambio de carrito
// o vendedor y el click solo hace window.location.href = cachedWaUrl.

(function () {
  if (window.__LF_VENDEDOR_WA_BOOTED__) return;
  window.__LF_VENDEDOR_WA_BOOTED__ = true;

  const SELECTOR_SELECT = '[data-lf-vendedor-select]';
  const SELECTOR_BLOCK = '[data-lf-vendedor-block]';
  const SELECTOR_TIENDA_BLOCK = '[data-lf-tienda-block]';
  const SELECTOR_NOMBRE_TIENDA = '[data-lf-nombre-tienda]';
  const SELECTOR_CIUDAD = '[data-lf-ciudad]';

  // Selectores estables de Shopify primero. El texto es solo fallback porque
  // cambia entre temas e idiomas (Pagar / Tramitar pedido / Finalizar compra…).
  const STABLE_CHECKOUT_SELECTORS =
    'button[name="checkout"], a[href*="/checkout"], [data-lf-checkout]';
  const TEXT_CHECKOUT_PATTERN =
    /(checkout|pagar|enviar pedido|finalizar compra|tramitar pedido|realizar pedido|proceder al pago)/;

  // Mapa de referencias → número de WhatsApp (sin prefijo 57; el código lo añade al construir la URL)
  // La clave coincide con el parámetro ?ref= del link que se comparte, ej: ...com.co/?ref=victor
  const SUBDOMAIN_MAP = {
    "victor": { nombre: "Victor", wa: "3217975076" },
    "jorge": { nombre: "Jorge", wa: "3217589458" },
    "edwin": { nombre: "Edwin", wa: "3187991533" },
    // Agrega aquí los demás: "maria": { nombre: "María", wa: "3207654321" },
  };
  let subdomainConfig = null; // se asigna en boot() al leer ?ref= de la URL

  // Valor del <option> "No tengo vendedor asignado" en lf-vendedor-select.liquid
  // (va al número general de La Frontera, no a un vendedor específico).
  const VENDEDOR_SIN_ASIGNAR = 'Dist. La Frontera';

  const ERROR_TEXT_DEFAULT = 'Selecciona un vendedor para continuar.';
  const ERROR_TEXT_PREPARING = 'Preparando pedido… toca de nuevo.';

  // El límite real de WhatsApp aplica a la URL final ya codificada
  // (wa.me/57...?text=<encodeURIComponent(mensaje)>), NO al texto crudo:
  // encodeURIComponent infla el mensaje ~2x (cada "•" → %E2%80%A2 = 9 chars,
  // cada salto de línea → %0A, cada espacio/tilde/$/= → 3-6 chars). Antes
  // medíamos el texto crudo con tope 7.500, lo que dejaba pasar pedidos cuya
  // URL superaba el límite y WhatsApp respondía "page not found" al abrirla
  // (típico en pedidos mayoristas con muchas referencias). Por eso el fallback
  // a PDF se decide por la longitud de la URL ya armada. 4.000 es conservador
  // y fiable en celular y computador; subirlo deja más pedidos en texto plano
  // pero acerca de nuevo al error.
  const SAFE_WA_URL_LENGTH = 4000;

  let lastSavedVendor = '';
  let lastSavedWA = '';
  let saveTimeout = null;

  let currentVendor = '';
  let currentWa = '';
  let currentNombreTienda = '';
  let currentCiudad = '';
  let cachedWaUrl = '';
  let cachedCart = null;
  let cachedLogo = null; // { dataUrl, ratio } — logo "Los Carameteños", solo para el PDF con tabla del flujo de subdominio
  let cachedFronteraLogo = null; // { dataUrl, ratio } — logo de La Frontera (settings.logo), solo para el PDF plano del flujo normal
  let rebuildTimeout = null;
  let cachedDetailPdfBlob = null; // PDF "plano" (flujo normal) cuando el pedido excede SAFE_WA_URL_LENGTH
  let cachedSubdomainPdfBlob = null; // PDF con tabla (flujo de subdominio)
  let currentNeedsPdf = false; // último resultado conocido de "el mensaje no cabe en WhatsApp"

  function storageSet(key, value) {
    try { sessionStorage.setItem(key, value); } catch (e) {}
    try { localStorage.setItem(key, value); } catch (e) {}
  }

  function storageGet(key) {
    try {
      return sessionStorage.getItem(key) || localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function formatCOPFromCents(cents) {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      maximumFractionDigits: 0
    }).format((Number(cents || 0)) / 100);
  }

  async function getCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('No se pudo leer /cart.js');
    return res.json();
  }

  async function updateCartAttributes(attrs) {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    });
    if (!res.ok) throw new Error(`No se pudo actualizar el carrito (${res.status})`);
    return res.json();
  }

  // Solo se usa en el flujo normal (con vendedor) — el de subdominio nunca
  // arma mensaje de WhatsApp (ver actuallyRebuildWaUrl).
  function buildMessageFromCart(cart, vendedorNombre) {
    let msg = `Hola,\n\nPedido La Frontera\n`;
    if (vendedorNombre) msg += `(Vendedor: ${vendedorNombre})\n`;
    msg += `\n`;

    (cart.items || []).forEach((item) => {
      const qty = item.quantity || 1;
      const name = item.product_title || item.title || 'Producto';
      const ref = item.sku ? ` - Ref: ${item.sku}` : '';
      const subtotal = formatCOPFromCents(item.line_price);
      msg += `• ${qty} ${name}${ref} = ${subtotal}\n`;
    });

    msg += `\nTotal Pedido ${formatCOPFromCents(cart.total_price)}\n`;
    return msg;
  }

  // Versión corta: se usa solo cuando el mensaje completo supera
  // SAFE_WA_URL_LENGTH. El detalle línea por línea se manda aparte como PDF.
  function buildShortMessageFromCart(cart, vendedorNombre) {
    let msg = `Hola,\n\nPedido La Frontera\n`;
    if (vendedorNombre) msg += `(Vendedor: ${vendedorNombre})\n`;

    const refCount = (cart.items || []).length;
    msg += `\nPedido de ${refCount} referencias — el detalle completo va en el PDF que te comparto a continuación.\n`;
    msg += `\nTotal Pedido ${formatCOPFromCents(cart.total_price)}\n`;
    return msg;
  }

  // Nombre de archivo estándar para cualquier PDF de pedido: Pedido_<algo>_<fecha>.pdf
  function buildPdfFilename(middle) {
    const hoy = new Date();
    const fecha = hoy.getDate() + '-' + (hoy.getMonth() + 1) + '-' + hoy.getFullYear();
    return 'Pedido_' + String(middle).replace(/\s+/g, '_') + '_' + fecha + '.pdf';
  }

  // Comparte un PDF directo a WhatsApp (u otra app) usando el selector nativo
  // del celular cuando está disponible; si no, cae a la descarga de siempre.
  // Debe llamarse dentro del handler de click (sin await previo) para
  // conservar el user-activation que exige navigator.share en iOS/Android.
  async function shareOrDownloadPdf(blob, filename) {
    if (blob) {
      try {
        const file = new File([blob], filename, { type: 'application/pdf' });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({ files: [file], title: 'Pedido La Frontera', text: 'Detalle del pedido' });
            return;
          } catch (shareErr) {
            if (shareErr && shareErr.name === 'AbortError') return; // el usuario canceló, no hacer nada
            // cualquier otro error: cae a descarga
          }
        }
      } catch (e) { /* File/Share API no disponible: cae a descarga */ }
    }

    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }

  function isCheckoutTrigger(el) {
    if (!el || el.nodeType !== 1 || typeof el.matches !== 'function') return false;
    if (el.matches(STABLE_CHECKOUT_SELECTORS)) return true;
    const text = (el.textContent || '').trim().toLowerCase();
    return TEXT_CHECKOUT_PATTERN.test(text);
  }

  function findCheckoutButtons() {
    const stable = Array.from(document.querySelectorAll(STABLE_CHECKOUT_SELECTORS));
    const byText = Array.from(document.querySelectorAll('button, a, [role="button"]')).filter((el) => {
      const text = (el.textContent || '').trim().toLowerCase();
      return TEXT_CHECKOUT_PATTERN.test(text);
    });
    return Array.from(new Set([...stable, ...byText]));
  }

  function setDisabled(el, disabled) {
    if (!el) return;
    const tag = el.tagName.toLowerCase();
    if (tag === 'a') {
      el.style.pointerEvents = disabled ? 'none' : '';
      el.style.opacity = disabled ? '0.5' : '';
      el.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    } else {
      el.disabled = !!disabled;
    }
  }

  function ensureError(container) {
    let msg = container.querySelector('[data-lf-vendedor-error]');
    if (!msg) {
      msg = document.createElement('p');
      msg.setAttribute('data-lf-vendedor-error', 'true');
      msg.style.marginTop = '0.5rem';
      msg.style.color = '#b00020';
      msg.style.display = 'none';
      msg.textContent = ERROR_TEXT_DEFAULT;
      container.appendChild(msg);
    }
    return msg;
  }

  // Debounced para coalescer ráfagas (p.ej. limpiar+agregar varios items seguidos).
  function rebuildWaUrl() {
    clearTimeout(rebuildTimeout);
    rebuildTimeout = setTimeout(actuallyRebuildWaUrl, 100);
  }

  async function actuallyRebuildWaUrl() {
    try {
      if (!currentWa) { cachedWaUrl = ''; return; }
      const cart = await getCart();
      if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) {
        cachedWaUrl = '';
        cachedDetailPdfBlob = null;
        currentNeedsPdf = false;
        updateDetailPdfButton(false);
        updateLargeOrderWarning(false);
        return;
      }

      cachedCart = cart;
      if (cart.token) storageSet('lf_cart_token', cart.token);

      // Subdominio: acá lo único que importa es el PDF (tabla + tienda/
      // ciudad) — "Enviar pedido" lo comparte/descarga directo (ver
      // initCheckoutHandler). Nunca se arma ni se usa una URL de WhatsApp,
      // así que cortamos acá y no perdemos tiempo armando mensajes.
      if (subdomainConfig) {
        updateSubdomainPdf();
        return;
      }

      const fullMsg = buildMessageFromCart(cart, currentVendor);
      // Medimos sobre la URL ya codificada, no sobre el texto crudo (ver
      // SAFE_WA_URL_LENGTH): es lo que WhatsApp realmente rechaza si es muy larga.
      const fullWaUrl = `https://wa.me/57${currentWa}?text=${encodeURIComponent(fullMsg)}`;
      const needsPdf = fullWaUrl.length > SAFE_WA_URL_LENGTH;
      currentNeedsPdf = needsPdf;

      cachedWaUrl = needsPdf
        ? `https://wa.me/57${currentWa}?text=${encodeURIComponent(buildShortMessageFromCart(cart, currentVendor))}`
        : fullWaUrl;
      cachedDetailPdfBlob = needsPdf ? buildPlainMessagePdf(fullMsg) : null;

      // El botón "Compartir PDF" aparte solo hace falta para "No tengo
      // vendedor asignado": ahí el botón "Enviar pedido" sigue abriendo
      // WhatsApp directo (al número general), así que el PDF necesita su
      // propia vía. Para un vendedor específico, "Enviar pedido" mismo pasa
      // a compartir el PDF (ver initCheckoutHandler) — no hace falta un
      // segundo botón.
      const isSinAsignar = currentVendor === VENDEDOR_SIN_ASIGNAR;
      updateDetailPdfButton(needsPdf && isSinAsignar);
      updateLargeOrderWarning(needsPdf);
    } catch (e) {
      console.error('LF: no se pudo precomputar URL WhatsApp:', e);
    }
  }

  async function saveVendorData(select) {
    const opt = select.options[select.selectedIndex];
    const vendedor = select.value || '';
    const wa = opt?.dataset?.vendedorWa || '';

    storageSet('lf_vendedor_nombre', vendedor);
    storageSet('lf_vendedor_wa', wa);

    if (!vendedor || !wa) return;

    currentVendor = vendedor;
    currentWa = wa;

    // Si ya está sincronizado en backend evitamos POST extra (Shopify limita /cart/update.js → 429).
    if (vendedor === lastSavedVendor && wa === lastSavedWA) {
      rebuildWaUrl();
      return;
    }

    lastSavedVendor = vendedor;
    lastSavedWA = wa;

    // El interceptor de fetch dispara rebuildWaUrl al resolverse esta llamada.
    await updateCartAttributes({ Vendedor: vendedor, VendedorWA: wa });
  }

  function validate(select, errorMsg) {
    if (subdomainConfig) {
      findCheckoutButtons().forEach((btn) => setDisabled(btn, false));
      return true;
    }
    const vendedor = select.value || '';
    const opt = select.options[select.selectedIndex];
    const wa = opt?.dataset?.vendedorWa || '';
    const ok = !!(vendedor && wa);

    findCheckoutButtons().forEach((btn) => setDisabled(btn, !ok));

    if (ok) {
      errorMsg.style.display = 'none';
    } else {
      errorMsg.textContent = ERROR_TEXT_DEFAULT;
      errorMsg.style.display = 'block';
    }
    return ok;
  }

  function loadLogoImage() {
    if (cachedLogo) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function () {
      try {
        const canvas = document.createElement('canvas');
        canvas.width  = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext('2d').drawImage(img, 0, 0);
        cachedLogo = {
          dataUrl: canvas.toDataURL('image/jpeg'),
          ratio: img.naturalWidth / img.naturalHeight,
        };
      } catch (e) { /* CORS: se usará fallback de texto */ }
    };
    img.src = 'https://cdn.shopify.com/s/files/1/0852/4860/6401/files/Los_Caramantenos.jpg';
  }

  // Logo de La Frontera (settings.logo del tema) para el PDF plano del flujo
  // normal — distinto del logo "Los Carameteños" que usa el PDF con tabla del
  // flujo de subdominio. La URL la expone theme.liquid en
  // window.__LF_FRONTERA_LOGO_URL__.
  function loadFronteraLogoImage() {
    if (cachedFronteraLogo || !window.__LF_FRONTERA_LOGO_URL__) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function () {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext('2d').drawImage(img, 0, 0);
        cachedFronteraLogo = {
          dataUrl: canvas.toDataURL('image/png'),
          ratio: img.naturalWidth / img.naturalHeight,
        };
      } catch (e) { /* CORS: se usará fallback de texto */ }
    };
    img.src = window.__LF_FRONTERA_LOGO_URL__;
  }

  function loadPdfLibraries() {
    loadLogoImage();
    loadFronteraLogoImage();
    if (window.jspdf) return;
    const s1 = document.createElement('script');
    s1.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
    s1.onload = function () {
      const s2 = document.createElement('script');
      s2.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js';
      // Si el carrito ya se resolvió antes de que jsPDF terminara de cargar,
      // reintenta una vez cargado para no dejar el botón/link de PDF sin
      // aparecer hasta el próximo cambio de carrito.
      s2.onload = function () { rebuildWaUrl(); };
      document.head.appendChild(s2);
    };
    document.head.appendChild(s1);
  }

  function buildPdfDoc() {
    if (!window.jspdf || !cachedCart) return null;
    const { jsPDF } = window.jspdf;
    const doc = new jsPDF('p', 'mm', 'letter');
    const cart = cachedCart;

    // Logo o fallback de texto
    var headerH = 22; // altura reservada para el encabezado antes de TIENDA/CIUDAD
    if (cachedLogo) {
      var logoH = 18;
      var logoW = logoH * cachedLogo.ratio;
      var logoX = (216 - logoW) / 2;
      doc.addImage(cachedLogo.dataUrl, 'JPEG', logoX, 4, logoW, logoH);
    } else {
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text('PLANILLA DE PEDIDOS', 108, 13, { align: 'center' });
    }

    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('TIENDA', 7, headerH + 7);
    doc.setFont('helvetica', 'normal');
    doc.line(20, headerH + 7, 100, headerH + 7);
    doc.text(currentNombreTienda || '', 22, headerH + 6);

    doc.setFont('helvetica', 'bold');
    doc.text('CIUDAD', 7, headerH + 15);
    doc.setFont('helvetica', 'normal');
    doc.line(20, headerH + 15, 100, headerH + 15);
    doc.text(currentCiudad || '', 22, headerH + 14);

    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('PROVEEDOR', 155, headerH + 7);
    doc.setFont('helvetica', 'normal');
    doc.text('Dist. La Frontera', 170, headerH + 14, { align: 'center' });

    const rows = (cart.items || []).map(function (item) {
      const qty = item.quantity || 1;
      const esDocena = qty >= 12 && qty % 12 === 0;
      return [
        esDocena ? '' : String(qty),
        esDocena ? String(qty / 12) : '',
        item.product_title || item.title || '',
        item.sku || '-',
        formatCOPFromCents(item.price),
        formatCOPFromCents(item.line_price),
      ];
    });

    doc.autoTable({
      head: [['UNIDAD', 'DOCENA', 'DESCRIPCION O NOMBRE DEL PRODUCTO', 'REFERENCIA', 'PRECIO UNIDAD', 'VALOR TOTAL']],
      body: rows,
      foot: [['', '', { content: 'TOTAL', colSpan: 3, styles: { halign: 'right', fontStyle: 'bold' } }, { content: formatCOPFromCents(cart.total_price), styles: { halign: 'right', fontStyle: 'bold' } }]],
      startY: headerH + 20,
      margin: { left: 5, right: 5 },
      styles: { fontSize: 8, cellPadding: 1.5, lineColor: [0, 0, 0], lineWidth: 0.2 },
      headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: 'bold', halign: 'center' },
      showFoot: 'lastPage',
      footStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0] },
      columnStyles: {
        0: { halign: 'center', cellWidth: 14 },
        1: { halign: 'center', cellWidth: 14 },
        2: { cellWidth: 'auto' },
        3: { halign: 'center', cellWidth: 26 },
        4: { halign: 'right', cellWidth: 24 },
        5: { halign: 'right', cellWidth: 24 },
      },
    });

    // Paginado "Página X de Y" — se añade después de la tabla para conocer el total
    const totalPaginas = doc.internal.getNumberOfPages();
    for (var p = 1; p <= totalPaginas; p++) {
      doc.setPage(p);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text('Página ' + p + ' de ' + totalPaginas, 108, 275, { align: 'center' });
    }

    // Fecha al pie de la última página
    doc.setPage(totalPaginas);
    const hoy = new Date();
    const fecha = hoy.getDate() + '/' + (hoy.getMonth() + 1) + '/' + hoy.getFullYear();
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(fecha, 7, doc.lastAutoTable.finalY + 6);

    return { doc, fecha };
  }

  // PDF "plano" del flujo normal (con vendedor): mismo texto del mensaje de
  // WhatsApp completo, paginado línea por línea. Se usa solo cuando el
  // mensaje no cabe en WhatsApp; no lleva tabla ni tienda/ciudad porque ese
  // formulario no existe en el flujo normal.
  function buildPlainMessagePdf(fullMessageText) {
    if (!window.jspdf) return null;
    const { jsPDF } = window.jspdf;
    const doc = new jsPDF('p', 'mm', 'letter');
    const marginX = 14;
    const marginBottom = 16;
    const pageHeight = doc.internal.pageSize.getHeight();
    const pageWidth = doc.internal.pageSize.getWidth();
    const lineHeight = 5;
    let y = 14;

    if (cachedFronteraLogo) {
      const logoH = 16;
      const logoW = logoH * cachedFronteraLogo.ratio;
      doc.addImage(cachedFronteraLogo.dataUrl, 'PNG', (pageWidth - logoW) / 2, y, logoW, logoH);
      y += logoH + 8;
    } else {
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text('PEDIDO LA FRONTERA', pageWidth / 2, y + 4, { align: 'center' });
      y += 14;
    }

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');

    fullMessageText.split('\n').forEach((line) => {
      const wrapped = doc.splitTextToSize(line, pageWidth - marginX * 2);
      (wrapped.length ? wrapped : ['']).forEach((wLine) => {
        if (y > pageHeight - marginBottom) {
          doc.addPage();
          y = 14;
        }
        doc.text(wLine, marginX, y);
        y += lineHeight;
      });
    });

    const totalPaginas = doc.internal.getNumberOfPages();
    for (let p = 1; p <= totalPaginas; p++) {
      doc.setPage(p);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text('Página ' + p + ' de ' + totalPaginas, pageWidth / 2, pageHeight - 8, { align: 'center' });
    }

    return doc.output('blob');
  }

  // Genera y cachea el PDF del flujo de subdominio (tabla + tienda/ciudad).
  // "Enviar pedido" lo comparte/descarga directo (ver initCheckoutHandler) —
  // no hay un botón de descarga aparte.
  function updateSubdomainPdf() {
    if (!subdomainConfig || !currentNombreTienda || !currentCiudad) return;
    const result = buildPdfDoc();
    if (!result) return;
    cachedSubdomainPdfBlob = result.doc.output('blob');
  }

  // Botón "Compartir detalle del pedido (PDF)" del flujo normal. Solo se
  // muestra cuando el mensaje de WhatsApp no cabe completo (ver
  // actuallyRebuildWaUrl / SAFE_WA_URL_LENGTH).
  function updateDetailPdfButton(show) {
    const btn = document.querySelector('[data-lf-pdf-share]');
    if (!btn) return;
    btn.style.display = show && cachedDetailPdfBlob ? '' : 'none';
  }

  // Texto rojo arriba de "Vaciar carrito" cuando el pedido no cabe en WhatsApp.
  function updateLargeOrderWarning(show) {
    const el = document.querySelector('[data-lf-large-order-warning]');
    if (!el) return;
    el.style.display = show ? '' : 'none';
  }

  function initDetailPdfButton() {
    const btn = document.querySelector('[data-lf-pdf-share]');
    if (!btn || btn.dataset.lfInitialized === 'true') return;
    btn.dataset.lfInitialized = 'true';
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      if (!cachedDetailPdfBlob) return;
      shareOrDownloadPdf(cachedDetailPdfBlob, buildPdfFilename('LaFrontera_' + (currentVendor || 'Pedido')));
    });
  }

  function validateTiendaFields() {
    const block = document.querySelector(SELECTOR_TIENDA_BLOCK);
    if (!block) return true;
    const nombre = (document.querySelector(SELECTOR_NOMBRE_TIENDA)?.value || '').trim();
    const ciudad = (document.querySelector(SELECTOR_CIUDAD)?.value || '').trim();
    const ok = !!(nombre && ciudad);
    const errorMsg = block.querySelector('[data-lf-tienda-error]');
    if (errorMsg) errorMsg.style.display = ok ? 'none' : 'block';
    findCheckoutButtons().forEach((btn) => setDisabled(btn, !ok));
    return ok;
  }

  function initTiendaFields() {
    if (!subdomainConfig) return;
    const block = document.querySelector(SELECTOR_TIENDA_BLOCK);
    if (!block || block.dataset.lfTiendaInitialized === 'true') return;
    block.dataset.lfTiendaInitialized = 'true';
    block.style.display = ''; // quitar el display:none — el CSS lo pone flex

    const nombreInput = document.querySelector(SELECTOR_NOMBRE_TIENDA);
    const ciudadInput = document.querySelector(SELECTOR_CIUDAD);

    if (nombreInput) {
      nombreInput.value = storageGet('lf_nombre_tienda') || '';
      currentNombreTienda = nombreInput.value.trim();
    }
    if (ciudadInput) {
      ciudadInput.value = storageGet('lf_ciudad') || '';
      currentCiudad = ciudadInput.value.trim();
    }

    if (currentNombreTienda && currentCiudad) rebuildWaUrl();
    validateTiendaFields();

    function onInput() {
      currentNombreTienda = (nombreInput?.value || '').trim();
      currentCiudad = (ciudadInput?.value || '').trim();
      storageSet('lf_nombre_tienda', currentNombreTienda);
      storageSet('lf_ciudad', currentCiudad);
      validateTiendaFields();
      if (currentNombreTienda && currentCiudad) { rebuildWaUrl(); updateSubdomainPdf(); }
    }

    nombreInput?.addEventListener('input', onInput);
    ciudadInput?.addEventListener('input', onInput);
  }

  function initVendedorBlock() {
    const block = document.querySelector(SELECTOR_BLOCK);
    const select = document.querySelector(SELECTOR_SELECT);
    if (!block || !select) return;

    if (subdomainConfig) {
      block.style.display = 'none';
      return;
    }

    if (select.dataset.lfInitialized === 'true') return;
    select.dataset.lfInitialized = 'true';

    const errorMsg = ensureError(block);
    validate(select, errorMsg);

    // Si Liquid ya renderizó un vendedor seleccionado, hidrata estado para que
    // la URL ya esté lista en el primer click sin requerir un change event.
    if (select.value) {
      const opt = select.options[select.selectedIndex];
      currentVendor = select.value;
      currentWa = opt?.dataset?.vendedorWa || '';
      if (currentWa) rebuildWaUrl();
    }

    select.addEventListener('change', () => {
      clearTimeout(saveTimeout);
      saveTimeout = setTimeout(async () => {
        try {
          await saveVendorData(select);
          validate(select, errorMsg);
        } catch (err) {
          console.error('LF vendedor error:', err);
          errorMsg.textContent = 'No se pudo actualizar el carrito.';
          errorMsg.style.display = 'block';
        }
      }, 400);
    });
  }

  // 100% síncrono. NO añadir await aquí: rompe el user-activation de iOS.
  function initCheckoutHandler() {
    if (document.body.dataset.lfCheckoutBound === 'true') return;
    document.body.dataset.lfCheckoutBound = 'true';

    document.addEventListener('click', function (e) {
      const target = e.target && e.target.closest && e.target.closest('button, a, [role="button"]');
      if (!target || !isCheckoutTrigger(target)) return;

      // Subdominio: no hay bloque de vendedor en el DOM. Acá lo único que
      // importa es el PDF (tabla + tienda/ciudad) — "Enviar pedido" lo
      // comparte/descarga directo, sin pasar por WhatsApp con texto.
      if (subdomainConfig) {
        e.preventDefault();
        e.stopPropagation();
        if (!validateTiendaFields()) {
          document.querySelector(SELECTOR_NOMBRE_TIENDA)?.focus();
          return;
        }
        if (cachedSubdomainPdfBlob) {
          shareOrDownloadPdf(cachedSubdomainPdfBlob, buildPdfFilename(currentNombreTienda || 'LaFrontera'));
          return;
        }
        const tiendaBlock = document.querySelector(SELECTOR_TIENDA_BLOCK);
        const tiendaError = tiendaBlock?.querySelector('[data-lf-tienda-error]');
        if (tiendaError) {
          tiendaError.textContent = ERROR_TEXT_PREPARING;
          tiendaError.style.display = 'block';
        }
        rebuildWaUrl();
        return;
      }

      const select = document.querySelector(SELECTOR_SELECT);
      const block = document.querySelector(SELECTOR_BLOCK);
      if (!select || !block) return;

      const errorMsg = ensureError(block);

      // Opción (b): siempre interceptamos; WhatsApp es el destino final, no /checkout.
      e.preventDefault();
      e.stopPropagation();

      const ok = validate(select, errorMsg);
      if (!ok) { select.focus(); return; }

      // Pedido grande + vendedor específico: "Enviar pedido" comparte el PDF
      // directo en vez de ir a WhatsApp (el mensaje completo no cabe, y acá
      // sí sabemos a qué vendedor iba, pero navigator.share no permite
      // preseleccionar el chat — el cliente elige a quién mandárselo).
      // Para "No tengo vendedor asignado" seguimos yendo a WhatsApp directo
      // (número general) y el PDF se comparte aparte con su propio botón.
      if (currentNeedsPdf && currentVendor !== VENDEDOR_SIN_ASIGNAR) {
        if (cachedDetailPdfBlob) {
          shareOrDownloadPdf(cachedDetailPdfBlob, buildPdfFilename('LaFrontera_' + (currentVendor || 'Pedido')));
          return;
        }
        // PDF aún no listo (carrito recién mutado): mismo patrón de re-tap que WhatsApp abajo.
        errorMsg.textContent = ERROR_TEXT_PREPARING;
        errorMsg.style.display = 'block';
        rebuildWaUrl();
        return;
      }

      if (cachedWaUrl) {
        // Misma pestaña → iOS y Android lanzan la app de WhatsApp de forma fiable.
        window.location.href = cachedWaUrl;
        return;
      }

      // Cache aún no lista (carrito recién mutado). Pedimos re-tap para conservar
      // el user-gesture; auto-navegar tras await sería bloqueado por iOS.
      errorMsg.textContent = ERROR_TEXT_PREPARING;
      errorMsg.style.display = 'block';
      rebuildWaUrl();
    }, true);

    // Calienta la cache: pointerdown llega ~100-300ms antes del click,
    // suficiente para que getCart() resuelva antes de que el usuario suelte.
    document.addEventListener('pointerdown', function (e) {
      const target = e.target && e.target.closest && e.target.closest('button, a, [role="button"]');
      if (target && isCheckoutTrigger(target)) rebuildWaUrl();
    }, true);
  }

  // Detecta mutaciones del carrito hechas por OTROS scripts (cart drawer, ajax
  // add-to-cart, apps de terceros) para mantener cachedWaUrl al día. Los eventos
  // tipo cart:updated no son universales entre temas; el patch de fetch sí lo es.
  function initCartFetchInterceptor() {
    if (window.__LF_FETCH_PATCHED__) return;
    window.__LF_FETCH_PATCHED__ = true;

    const origFetch = window.fetch && window.fetch.bind(window);
    if (!origFetch) return;

    window.fetch = function (input, init) {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
      const isMutation =
        method !== 'GET' &&
        /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url);

      const promise = origFetch(input, init);
      if (isMutation) promise.then(() => rebuildWaUrl(), () => {});
      return promise;
    };

    ['cart:updated', 'cart:refresh', 'cart:change'].forEach((ev) => {
      document.addEventListener(ev, rebuildWaUrl);
    });
  }

  function bootstrapFromStorage() {
    const v = storageGet('lf_vendedor_nombre') || '';
    const w = storageGet('lf_vendedor_wa') || '';
    if (v && w) {
      currentVendor = v;
      currentWa = w;
      rebuildWaUrl();
    }
  }

  function boot() {
    // Detectar ?ref= en la URL y guardarlo en sessionStorage para que persista en navegación
    const urlRef = new URLSearchParams(window.location.search).get('ref');
    if (urlRef) storageSet('lf_subdomain_ref', urlRef);
    const ref = storageGet('lf_subdomain_ref') || '';
    subdomainConfig = SUBDOMAIN_MAP[ref] || null;

    initCartFetchInterceptor();
    // jsPDF se carga siempre (no solo en subdominio): el flujo normal también
    // puede necesitar el PDF plano si un pedido supera SAFE_WA_URL_LENGTH.
    loadPdfLibraries();
    if (subdomainConfig) {
      currentVendor = subdomainConfig.nombre;
      currentWa = subdomainConfig.wa;
      rebuildWaUrl();
    } else {
      bootstrapFromStorage();
    }
    initVendedorBlock();
    initTiendaFields();
    initDetailPdfButton();
    initCheckoutHandler();

    const observer = new MutationObserver(() => {
      initVendedorBlock();
      initTiendaFields();
      initDetailPdfButton();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
